import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, Gift, Package, Check, Loader2, ArrowRight, Mail, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import MagicCodeSignIn from '../components/MagicCodeSignIn';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

const PLANS = [
  { id: 'free', name: 'Free', price: '$0', period: '/forever', items: 20, description: 'Perfect for getting started', highlight: false },
  { id: 'starter', name: 'Starter', price: '$2.99', period: '/month', items: 100, description: 'For the growing crafter', highlight: true },
  { id: 'crafter', name: 'Crafter', price: '$4.99', period: '/month', items: 200, description: 'Most popular', highlight: false },
  { id: 'artisan', name: 'Artisan', price: '$9.99', period: '/month', items: 500, description: 'For serious collectors', highlight: false },
];

const ReferralLandingPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [authMethod, setAuthMethod] = useState(null); // null, 'otp'
  const [applyingReferral, setApplyingReferral] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/billing/referral/validate/${code}`);
        if (!res.ok) {
          const data = await res.clone().json();
          setError(data.detail || 'Invalid referral link');
          return;
        }
        setReferralData(await res.clone().json());
      } catch {
        setError('Could not verify referral link. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (code) validate();
    else { setError('No referral code provided'); setLoading(false); }
  }, [code]);

  const handleOAuthSignUp = (provider = 'google') => {
    localStorage.setItem('pending_referral_code', code.toUpperCase());
    if (selectedPlan !== 'free') {
      localStorage.setItem('pending_plan', selectedPlan);
    }
    const origin = window.location.origin;
    window.location.href = `${BACKEND_URL}/api/auth/${provider}/login?origin=${encodeURIComponent(origin)}`;
  };

  const handleOtpSuccess = async (data) => {
    if (data.session_token) {
      setApplyingReferral(true);
      if (selectedPlan !== 'free') {
        localStorage.setItem('pending_plan', selectedPlan);
      }
      await refreshUser();
      navigate('/stash');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-sage mx-auto" />
          <p className="mt-4 text-charcoal">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream">
        <nav className="bg-white border-b border-beige">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-6 w-6 text-sage" />
              <span className="text-xl font-light text-charcoal">Kura Stash</span>
            </div>
          </div>
        </nav>
        <div className="flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-light text-charcoal mb-3">Link Not Available</h1>
            <p className="text-gray-soft mb-8">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-sage hover:bg-sage-dark" data-testid="go-home-btn">
              Visit Kura Stash
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Nav */}
      <nav className="bg-white border-b border-beige">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <Sparkles className="h-6 w-6 text-sage" />
            <div className="flex flex-col">
              <span className="text-xl font-light text-charcoal leading-tight">Kura Stash</span>
              <span className="text-xs text-gray-soft">from <a href="https://kura.shop" target="_blank" rel="noopener noreferrer" className="text-sage hover:text-sage-dark underline">Kura Studio</a></span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 pt-10 sm:pt-16 pb-16">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="relative w-20 h-20 mx-auto mb-6">
            {referralData?.referrer_picture ? (
              <img src={referralData.referrer_picture} alt="" className="w-20 h-20 rounded-full border-4 border-sage/20 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-sage-light flex items-center justify-center border-4 border-sage/20">
                <Gift className="h-8 w-8 text-sage" />
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 bg-sage text-white rounded-full p-1.5">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-light text-charcoal mb-3" data-testid="referral-heading">
            {referralData?.referrer_first_name} invited you to Kura Stash
          </h1>
          <p className="text-base text-gray-soft max-w-lg mx-auto leading-relaxed">
            Organize your craft supplies and get AI-powered design inspiration.
            Start with <strong className="text-charcoal">{referralData?.base_free_grant} free items</strong> — no credit card needed.
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl border border-beige p-5 sm:p-6 mb-8">
          <div className="space-y-3">
            {[
              { icon: Package, text: `${referralData?.base_free_grant} free item slots to catalog your supplies` },
              { icon: Sparkles, text: 'AI-powered design suggestions from your collection' },
              { icon: Gift, text: `Your friend earns ${referralData?.free_items_per_referral} bonus items when you join` },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-sage-light/40 flex items-center justify-center mt-0.5">
                  <Icon className="h-4 w-4 text-sage" />
                </div>
                <p className="text-charcoal text-sm sm:text-base">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-charcoal mb-4 text-center">Choose your plan</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PLANS.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                  selectedPlan === plan.id
                    ? 'border-sage bg-sage/5 ring-1 ring-sage/30'
                    : 'border-beige bg-white hover:border-sage/40'
                }`}
                data-testid={`plan-${plan.id}-btn`}
              >
                {plan.highlight && (
                  <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-sage text-white text-[10px] px-2 py-0.5">
                    Popular
                  </Badge>
                )}
                <p className="font-semibold text-charcoal text-base">{plan.price}</p>
                <p className="text-[11px] text-gray-soft">{plan.period}</p>
                <p className="text-xs text-charcoal mt-2 font-medium">{plan.items} items</p>
                {selectedPlan === plan.id && (
                  <div className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-sage" />
                  </div>
                )}
              </button>
            ))}
          </div>
          {selectedPlan !== 'free' && (
            <p className="text-center text-xs text-gray-soft mt-3">
              You'll be redirected to set up billing after creating your account.
            </p>
          )}
        </div>

        {/* Sign Up Methods */}
        <Card className="border-beige">
          <CardContent className="p-6 sm:p-8">
            {authMethod === 'otp' ? (
              <div>
                <button
                  onClick={() => setAuthMethod(null)}
                  className="text-sm text-sage hover:text-sage-dark mb-4 flex items-center gap-1"
                  data-testid="referral-back-btn"
                >
                  &larr; Back to sign-up options
                </button>
                {applyingReferral ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-sage mx-auto mb-3" />
                    <p className="text-charcoal">Applying your referral and setting up your account...</p>
                  </div>
                ) : (
                  <MagicCodeSignIn onSuccess={handleOtpSuccess} />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={() => handleOAuthSignUp('google')}
                  className="w-full bg-sage hover:bg-sage-dark text-white py-3 text-base"
                  data-testid="referral-signup-google-btn"
                >
                  Sign Up with Google <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  onClick={() => handleOAuthSignUp('facebook')}
                  variant="outline"
                  className="w-full border-2 border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/5 py-3 text-base"
                  data-testid="referral-signup-facebook-btn"
                >
                  Sign Up with Facebook <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-beige"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-xs text-gray-soft">or</span>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    localStorage.setItem('pending_referral_code', code.toUpperCase());
                    setAuthMethod('otp');
                  }}
                  variant="outline"
                  className="w-full border-2 border-charcoal/20 text-charcoal hover:bg-charcoal/5 py-3 text-base"
                  data-testid="referral-signup-otp-btn"
                >
                  <Mail className="h-4 w-4 mr-2" /> Sign Up with Email
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Social proof */}
        <div className="text-center mt-8">
          <div className="flex items-center justify-center gap-1 mb-2">
            {[1,2,3,4,5].map(i => (
              <Check key={i} className="h-4 w-4 text-sage" />
            ))}
          </div>
          <p className="text-sm text-gray-soft">
            Trusted by paper crafters for smarter supply organization
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralLandingPage;
